import React, { useState, useEffect } from 'react';
import { Eye, TrendingUp } from 'lucide-react';
import Layout from '../components/Layout';
import MetricCard from '../components/MetricCard';
import YouTubeChart from '../components/YouTubeChart';
import { dashboardApi } from '../api/dashboard';

const SocialSentiment = () => {
  const [loading, setLoading] = useState(true);
  const [youtubeStats, setYoutubeStats] = useState([]);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    fetchYouTubeData();
  }, []);

  const handleYearChange = async (year) => {
    const yearToFetch = year === new Date().getFullYear() ? null : year;
    setSelectedYear(yearToFetch);

    try {
      const youtubeData = await dashboardApi.getYouTubeMonthlyStats(yearToFetch);
      setYoutubeStats(youtubeData?.monthly_stats || []);
    } catch (error) {
      console.error('Error fetching YouTube data for year:', year, error);
    }
  };

  const fetchYouTubeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const youtubeData = await dashboardApi.getYouTubeMonthlyStats();
      setYoutubeStats(youtubeData?.monthly_stats || []);

    } catch (err) {
      console.error('Error fetching YouTube data:', err);
      setError('Failed to load YouTube data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Format large numbers for display
  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString();
  };

  // Calculate metrics from YouTube data
  const calculateMetrics = () => {
    // Current month views
    const latestMonth = youtubeStats?.[youtubeStats.length - 1];
    const currentMonthViews = latestMonth?.total_views || 0;

    // Previous month views for comparison
    const previousMonth = youtubeStats?.[youtubeStats.length - 2];
    const previousMonthViews = previousMonth?.total_views || 0;

    // Calculate growth
    let viewsGrowth = 0;
    if (previousMonthViews > 0) {
      viewsGrowth = ((currentMonthViews - previousMonthViews) / previousMonthViews * 100).toFixed(1);
    }

    // Get current month name
    let currentMonthName = 'This Month';
    if (latestMonth?.month) {
      const date = new Date(latestMonth.month + '-01');
      currentMonthName = date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    }

    // Total views across all months
    const totalViews = youtubeStats.reduce((sum, month) => sum + (month.total_views || 0), 0);

    return {
      currentMonthViews: formatNumber(currentMonthViews),
      currentMonthName,
      viewsGrowth,
      totalViews: formatNumber(totalViews)
    };
  };

  const { currentMonthViews, currentMonthName, viewsGrowth, totalViews } = calculateMetrics();

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-400 mb-2">Error loading social sentiment data</p>
            <p className="text-dark-400 text-sm mb-4">{error}</p>
            <button
              onClick={fetchYouTubeData}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-white">Social Sentiment</h1>
          <p className="text-dark-300">YouTube channel performance and social media metrics</p>
        </div>

        {/* Top Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MetricCard
            title={`${currentMonthName} Views`}
            value={currentMonthViews}
            icon={Eye}
            color="green"
            loading={loading}
            subtitle={viewsGrowth !== 0 ? `${viewsGrowth > 0 ? '+' : ''}${viewsGrowth}% vs last month` : null}
          />
          <MetricCard
            title="Total YouTube Views"
            value={totalViews}
            icon={TrendingUp}
            color="blue"
            loading={loading}
            subtitle="All time across all channels"
          />
        </div>

        {/* YouTube Chart */}
        <YouTubeChart
          data={youtubeStats}
          loading={loading}
          onYearChange={handleYearChange}
        />

      </div>
    </Layout>
  );
};

export default SocialSentiment;
